import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import {
  scheduledQuery,
  type ScheduledMessageSummary,
  type ScheduledStatus,
} from '@/lib/scheduled';
import { rulesQuery } from '@/lib/rules';
import { templatesQuery } from '@/lib/templates';
import { cn } from '@/lib/utils';
import { usePageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { ScheduledStatusBadge } from '@/components/StatusBadges';
import { SCHEDULED_STATUS_TONE } from '@/lib/status-tone';

export const Route = createFileRoute('/_app/automation-scheduled')({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(rulesQuery()),
      context.queryClient.ensureQueryData(templatesQuery()),
    ]),
  component: ScheduledPage,
});

const STATUSES: ScheduledStatus[] = ['pending', 'sent', 'failed', 'cancelled'];

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function TruncatedError({ text }: { text: string }) {
  const truncated = text.length > 60 ? text.slice(0, 60) + '…' : text;
  return (
    <span title={text} className="cursor-help text-xs text-destructive">
      {truncated}
    </span>
  );
}

const LIMIT = 50;

export function ScheduledTable({
  items,
  ruleMap,
  templateMap,
  emptyText = 'No scheduled messages.',
}: {
  items: ScheduledMessageSummary[];
  ruleMap: Map<string, string>;
  templateMap: Map<string, string>;
  emptyText?: string;
}) {
  return (
    <div className="overflow-x-auto overflow-hidden rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--border-soft)] bg-[var(--surface-sunken)] text-left text-xs font-semibold uppercase tracking-[0.06em] text-muted">
            <th className="px-4 py-3">Scheduled for</th>
            <th className="px-4 py-3">Rule</th>
            <th className="px-4 py-3">Template</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Attempts</th>
            <th className="px-4 py-3">Last error</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-8 text-center text-muted">
                {emptyText}
              </td>
            </tr>
          )}
          {items.map((item) => (
            <tr
              key={item.id}
              className="border-b border-[var(--border-soft)] transition last:border-0 hover:bg-[var(--surface-sunken)]"
            >
              <td className="px-4 py-3 tabular-nums">{formatDateTime(item.scheduled_for)}</td>
              <td className="px-4 py-3 text-muted">
                {ruleMap.get(item.rule_id) ?? item.rule_id.slice(0, 8) + '…'}
              </td>
              <td className="px-4 py-3 text-muted">
                {templateMap.get(item.template_id) ?? item.template_id.slice(0, 8) + '…'}
              </td>
              <td className="px-4 py-3">
                <ScheduledStatusBadge status={item.status as ScheduledStatus} />
              </td>
              <td className="px-4 py-3 tabular-nums">{item.attempts}</td>
              <td className="px-4 py-3">
                {item.last_error ? <TruncatedError text={item.last_error} /> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ScheduledPage() {
  const [status, setStatus] = useState<ScheduledStatus | undefined>(undefined);
  const [page, setPage] = useState(0);

  const { data, isLoading, isError } = useQuery(
    scheduledQuery({ status, limit: LIMIT, offset: page * LIMIT }),
  );

  const { data: rulesData } = useQuery(rulesQuery());
  const { data: templatesData } = useQuery(templatesQuery());

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / LIMIT);

  const ruleMap = new Map<string, string>(
    (rulesData?.items ?? []).map((r) => [r.id, r.name]),
  );
  const templateMap = new Map<string, string>(
    (templatesData?.items ?? []).map((t) => [t.id, t.name]),
  );

  function handleStatusChip(s: ScheduledStatus) {
    setStatus((prev) => (prev === s ? undefined : s));
    setPage(0);
  }

  // Title lives in the Topbar (navTitle → "Scheduled"); publish the live queue
  // count as the subtitle (falls back to navSubtitle until data loads).
  usePageHeader({
    subtitle: data
      ? `${total} scheduled message${total === 1 ? '' : 's'}`
      : undefined,
  });

  return (
    <>
      <div className="mb-4 flex gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => handleStatusChip(s)}
            className={cn(
              'rounded-full border px-3 py-1 text-xs font-medium transition',
              status === s
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border text-muted hover:bg-muted/20',
            )}
          >
            {SCHEDULED_STATUS_TONE[s].label}
          </button>
        ))}
        {status && (
          <button
            type="button"
            onClick={() => { setStatus(undefined); setPage(0); }}
            className="ml-auto text-xs text-muted hover:underline"
          >
            Clear filter
          </button>
        )}
      </div>

      {isLoading && <p className="text-muted">Loading…</p>}
      {isError && (
        <div
          role="alert"
          className="mb-4 flex items-start gap-3 rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-4 py-3 text-sm text-destructive"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
          <span>Failed to load scheduled messages.</span>
        </div>
      )}

      {!isLoading && !isError && (
        <>
          <ScheduledTable
            items={items}
            ruleMap={ruleMap}
            templateMap={templateMap}
            emptyText={
              status
                ? `No ${SCHEDULED_STATUS_TONE[status].label.toLowerCase()} messages.`
                : 'No scheduled messages.'
            }
          />

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-muted">
                {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, total)} of {total}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
